import React from "react";
import PropTypes from "prop-types";
import {withStyles} from "@mui/styles";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import "react-daterange-picker/dist/css/react-calendar.css";
import spinner from '../../assign-prescriptions/images/spinner.gif';
import Dashboard from './Dashboard';
import {toast} from "react-toastify";
import {IconButton} from "@mui/material";
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import moment from 'moment-timezone';
import {withTranslation} from "react-i18next";
import ILocalAGGrid, {
  getDefaultColDef,
} from "../../grid/iLocalAGGrid";
import {getDefaultDateRange, getDateRangeByPreset} from "../../../utils/datetime.utils";
import enLocale from "date-fns/locale/en-US";
import frLocale from "date-fns/locale/fr";
import Divider from '@mui/material/Divider';


class EnhancedTableHead extends React.Component {
  createSortHandler = property => event => {
    this.props.onRequestSort(event, property);
  };

  render() {
    const {order, orderBy, t} = this.props;
    const currentLang = localStorage.getItem('i18nextLng');

    const rows = [
      { id: "store", numeric: false, disablePadding: false, label: t("Report.Temperature.Store"), padding: '10px' },
      { id: "box", numeric: false, disablePadding: false, label: t("Report.Temperature.Location"), padding: '10px' },
      currentLang === 'en' && { id: "side", numeric: false, disablePadding: false, label: t("Report.Temperature.Side"), padding: '10px' },
      { id: "port", numeric: false, disablePadding: false, label: t("Report.Temperature.Port"), padding: '10px' },
      { id: "temperature", numeric: false, disablePadding: false, label: t("Report.Temperature.Temperature"), padding: '10px', textAlign: 'center' },
      { id: "connectwith", numeric: false, disablePadding: false, label: t("Report.Temperature.ConnectWith"), padding: '10px' },
      { id: "action", numeric: false, disablePadding: false, label: "", padding: '10px' },
    ];

    return (
      <TableHead>
        <TableRow>
          {rows.map(row => {
            return (
              <TableCell
                key={row.id}
                numeric={row.numeric}
                padding={row.disablePadding ? "none" : "default"}
                sortDirection={orderBy === row.id ? order : false}
                style={{ padding: row.padding, color: 'rgba(0, 0, 0, 0.54)' }}
              >
                <Tooltip
                  title={t("Global.Label.Filter.Sort")}
                  placement={row.numeric ? "bottom-end" : "bottom-start"}
                  enterDelay={300}
                >
                  <TableSortLabel
                    active={orderBy === row.id}
                    direction={order}
                    onClick={this.createSortHandler(row.id)}
                  >
                    {row.label}
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            );
          }, this)}
        </TableRow>
      </TableHead>
    );
  }
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
};
EnhancedTableHead = withTranslation()(EnhancedTableHead);

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const findOffset = moment().tz(timeZone).utcOffset();
const defaultZone = `(UTC ${findOffset}) ${timeZone}`;

const styles = theme => ({
  root: {
    width: "100%",
    position: 'relative'
  },
  table: {
    minWidth: 1020
  },
  tableWrapper: {
    overflowX: "auto"
  },
  dob: {
    paddingTop: '25px',
  },
  headerdiv: {
    paddingLeft: '15%',
    paddingTop: '10'
  },
  padding10px: {
    padding: '10px'
  }
  ,
  formControl: {
    margin: theme.spacing.unit,
    minWidth: 120,
    maxWidth: 390,
  },
  logoWrapper:
  {
    position: 'absolute',
    width: '89%',
    height: '70%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.5)',
    zIndex: '7869'
  },
  spinner: {
    position: 'absolute',
    background: 'rgba(255,255,255,.4)',
    width: '100%',
    height: '100%',
    zIndex: '9999',
    top: 0,
    left: 0,
  },
  spinnerImage: {
    top: 'calc(50% - 32.5px)',
    left: 'calc(50% - 65px)',
    position: 'absolute',
    width: '130px',
    userSelect: 'none'
  },
  hidden: {
    display: 'none'
  },
  primaryButton: {
    background: '#6cca98',
    color: '#fff',
    borderRadius: '5px',
    fontSize: 'large',
    '&:hover': {
      background: '#6cca98',
      color: '#fff',
      borderRadius: '5px',
    },
    '&:disabled': {
      color: 'rgba(0, 0, 0, 0.38)',
      cursor: 'not-allowed',
      background: '#E4E5E8',
      borderRadius: '5px',
    }
  }
});

class EnhancedTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      order: "desc",
      dob: '',
      orderBy: "created_on",
      page: 0,
      rowsPerPage: 5,
      loader: false,
      isDataEmpty: true,
      selectedObject: null,
      isUserValidate: false,
      dashboardData: [[]],
      storeData: [],
      startDate: '',
      endDate: '',
      isDownloadInProcess: false,
      fromdate: null,
      todate: null,
      dateRangePreset: 'last7days',
      locale: localStorage.getItem('i18nextLng') === 'en' ? enLocale : frLocale
    };
  }

  componentDidMount() {
    // Set default date range (T-30 days to today)
    const defaultDates = getDefaultDateRange();
    this.setState({ 
      loader: true,
      fromdate: defaultDates.fromDate,
      todate: defaultDates.toDate,
      dateRangePreset: 'last7days'
    });
    this.getDashboardData();
  }

  fetchDashboardData = async (data) => {
    const history = await fetch(process.env.REACT_APP_BASEURL + 'temperature/history/by/expansion', {
      method: 'post',
      withCredentials: true,
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(response => {
        try {
          return response.json();
        } catch (error) {
          return null;
        }
      })
      .then(res => {
        return res;
      })
    return history;
  }

  convertToTimeZone = (requestDate) => {
    const currentUserLang = localStorage.getItem('i18nextLng');
    let zone;
    zone = sessionStorage.getItem('timeZone');
    const date = moment(new Date(requestDate));
    if (['null', null, 'undefined', undefined].includes(zone)) {
      zone = defaultZone;
    }
    return currentUserLang === 'en' ? date.tz(zone.substring(zone.lastIndexOf(' ') + 1)).format('MM/DD/YYYY hh:mm:ss A') : date.tz(zone.substring(zone.lastIndexOf(' ') + 1)).format('DD/MM/YYYY hh:mm:ss A');
  }
  getDashboardData = () => {
    fetch(process.env.REACT_APP_BASEURL + 'temperature/monitoring/report', {
      method: 'get',
      withCredentials: true,
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        try {
          return response.json();
        } catch (error) {
          return null;
        }
      })
      .then(async (res) => {
        this.setState({ storeData: res });
        const id = [];
        for (let i = 0; i < res.length; i++) {
          id.push(res[i].expansionId)
        }

        let startDate = new Date();
        startDate.toLocaleString();
        startDate.setDate(startDate.getDate() - 3);
        let endDate = new Date();
        endDate.toLocaleDateString();
        let data = {
          "startdate": startDate,
          "enddate": endDate,
          "expansionId": id,
          "parent_account_id": sessionStorage.getItem('parentAccountId')
        }
        this.setState({ startDate: startDate, endDate: endDate })
        let chartHistory = []
        for (let i = 0; i < id.length; i++) {
          data.expansionId = id[i];
          let item = {};
          item[id[i]] = await this.fetchDashboardData(data);
          chartHistory.push(item);
        }
        this.setState({ dashboardData: chartHistory, loader: chartHistory && chartHistory.total && false });
      });
  }

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = "desc";

    if (this.state.orderBy === property && this.state.order === "desc") {
      order = "asc";
    }

    this.setState({ order, orderBy });
  };

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: event.target.value });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: event.target.value });
  };

  downloadPopup = (event, n) => {
    const requestObj = {
      fromdate: moment(this.state.fromdate).tz(timeZone),
      todate: moment(this.state.todate).tz(timeZone),
      expansionId: n.expansionId,
      parent_account_id: sessionStorage.getItem('parentAccountId')
    }
    event && event.preventDefault();
    if (requestObj.fromdate === undefined || requestObj.fromdate === null || requestObj.fromdate === "") {
      toast.error(this.props.t('Toast.Error.SelectFromDate'));
      return;
    }
    if (requestObj.todate === undefined || requestObj.todate === null || requestObj.todate === "") {
      toast.error(this.props.t('Toast.Error.SelectToDate'));
      return;
    }
    this.setState({ loader: true });
    fetch(process.env.REACT_APP_BASEURL + "CSV/TemperatureHistoryReport", {
      method: "POST",
      withCredentials: true,
      headers: {
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestObj),
    })
      .then((response) => {
        try {
          return response.json();
        } catch (error) {
          return null;
        }
      })
      .then((res) => {
        this.setState({ loader: false });
        let csvRow = [];
        let A = [
          [
            "Store",
            "Location",
            "Side",
            "Temperature",
            "Connect With",
            "Date",
          ],
        ];
        let re = res && (res.Results || res.results) && (res.Results || res.results).length > 0 
          ? (res.Results || res.results) 
          : [];
        if (re && re.length > 0) {
          for (let item = 0; item < re.length; item++) {
            A.push([
              re[item].store !== undefined ? re[item].store : "",
              re[item].boxname !== undefined ? re[item].boxname : "",
              re[item] && re[item].direction === 1 ? "Left" : "Right",
              re[item].temperature !== undefined ? re[item].temperature : "",
              re[item].connectedwith !== undefined ? re[item].connectedwith : "",
              re[item].temperature_timestamp
                ? this.convertToTimeZone(re[item].temperature_timestamp)
                : "",
            ]);
          }
        }
        for (let i = 0; i < A.length; i++) {
          // Escape double quotes and commas in CSV fields
          csvRow.push(
            A[i]
              .map(field =>
                typeof field === "string" && (field.includes(",") || field.includes('"'))
                  ? `"${field.replace(/"/g, '""')}"`
                  : field
              )
              .join(",")
          );
        }
        let csvString = csvRow.join("\r\n");
        let blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        let a = document.createElement("a");
        if (navigator.msSaveBlob) { // For IE 10+
          navigator.msSaveBlob(blob, "TemperatureMonitoring.csv");
        } else {
          let url = URL.createObjectURL(blob);
          a.href = url;
          a.download = "TemperatureMonitoring.csv";
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }, 0);
        }
      });
  }

  onFromDateSelect = date => {
    const dateObj = date ? moment(date) : null;
    this.setState({ fromdate: dateObj, dateRangePreset: '' });
    if (dateObj && this.state.todate) {
      this.refreshDashboardData();
    }
  };

  onToDateSelect = date => {
    const dateObj = date ? moment(date) : null;
    this.setState({ todate: dateObj, dateRangePreset: '' });
    if (dateObj && this.state.fromdate) {
      this.refreshDashboardData();
    }
  };
  
  handleBlurfrom = (value, date) => {
    const dateObj = date ? moment(date) : null;
    this.setState({
      fromdate: dateObj,
      dateRangePreset: ''
    });
    if (dateObj && this.state.todate) {
      this.refreshDashboardData();
    }
  }
  
  handleBlurtodate = (value, date) => {
    const dateObj = date ? moment(date) : null;
    this.setState({
      todate: dateObj,
      dateRangePreset: ''
    });
    if (dateObj && this.state.fromdate) {
      this.refreshDashboardData();
    }
  }
  
  handleDateRangePresetChange = (event) => {
    const preset = event.target.value;
    if (!preset) return;
    
    const dateRange = getDateRangeByPreset(preset);
    this.setState({
      fromdate: dateRange.fromDate,
      todate: dateRange.toDate,
      dateRangePreset: preset
    }, () => {
      this.refreshDashboardData();
    });
  };
  
  refreshDashboardData = () => {
    if (!this.state.fromdate || !this.state.todate || !this.state.storeData || this.state.storeData.length === 0) return;
    
    this.setState({ loader: true });
    const id = [];
    for (let i = 0; i < this.state.storeData.length; i++) {
      id.push(this.state.storeData[i].expansionId);
    }

    const startDate = this.state.fromdate.toDate();
    const endDate = this.state.todate.toDate();
    let data = {
      "startdate": startDate,
      "enddate": endDate,
      "expansionId": id,
      "parent_account_id": sessionStorage.getItem('parentAccountId')
    };
    this.setState({ startDate: startDate, endDate: endDate });
    
    Promise.all(id.map(async (expansionId) => {
      data.expansionId = expansionId;
      let item = {};
      item[expansionId] = await this.fetchDashboardData(data);
      return item;
    })).then(chartHistoryData => {
      this.setState({ dashboardData: chartHistoryData, loader: false });
    });
  };

  render() {
    const {classes, t} = this.props;
    const { storeData, rowsPerPage } = this.state;
    const currentLang = localStorage.getItem('i18nextLng');
    return (
      <div>
        {
          this.state.loader ?
            <div className={classes.spinner}>
              <img className={classes.spinnerImage} src={spinner} alt="loading" />
            </div> :
            null
        }
        <Divider style={{ marginTop: '10px', height: '5px' }} />
         <div className={classes.tableWrapper}>
          <ILocalAGGrid
            columnDefs={[
              { field: "store", headerName: t("Report.Temperature.Store"), colId: "store" },
              { field: "box", headerName: t("Report.Temperature.Location"), colId: "box" },
              ...(currentLang === "en"
                ? [{ field: "side", headerName: t("Report.Temperature.Side"), colId: "side" }]
                : []),
              { field: "comPort", headerName: t("Report.Temperature.Port"), colId: "port" },
              {
                field: "temperature",
                headerName: t("Report.Temperature.Temperature"),
                colId: "temperature",
                filter: false,
                sortable: false,
                minWidth: 300,
                cellRenderer: (params) => {
                  const n = params.data;
                  if (!n) return null;
                  return (
                    <span>
                      {(this.state &&
                        this.state.dashboardData &&
                        this.state.dashboardData.map &&
                        this.state.dashboardData.map((item) => {
                          if (n.expansionId === Object.keys(item)[0]) {
                            return (
                              <Dashboard
                                margin="2%"
                                key={Object.keys(item)[0]}
                                data={item && item[n.expansionId]}
                                startDate={this.state.startDate}
                                endDate={this.state.endDate}
                                convertTimeZone={this.convertToTimeZone}
                                tempUnit={n.tempUnit}
                              />
                            );
                          }
                          return null;
                        })) || null}
                    </span>
                  );
                },
              },
              { field: "connectedWith", headerName: t("Report.Temperature.ConnectWith"), colId: "connectwith" },
              {
                field: "action",
                headerName: "",
                colId: "action",
                filter: false,
                sortable: false,
                cellRenderer: (params) => (
                  <Tooltip key="single-edit" title={t("Report.Temperature.DownloadReportButton")}>
                    <IconButton
                      aria-label="downloadreport"
                      onClick={(event) => this.downloadPopup(event, params.data)}
                      style={{ fontSize: "2rem" }}
                      sx={() => ({ "&:hover": { backgroundColor: "#6cca98", color: "#fff" } })}
                    >
                      <DownloadForOfflineOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                ),
              },
            ]}
            defaultColDef={getDefaultColDef()}
            rowModelType="clientSide"
            rowData={storeData || []}
            paginationPageSize={rowsPerPage || 10}
            paginationPageSizeSelector={[10, 20, 25]}
            rowHeight={120}
            getRowId={(params) =>
              (params.data && (params.data.expansionId || params.data.id || params.data.uuid)) ||
              String(params.data && JSON.stringify(params.data).slice(0,40))
            }
          />

        </div>

      </div>
    );
  }
}

EnhancedTable.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles, { withTheme: true })(withTranslation()(EnhancedTable));



// WEBPACK FOOTER //
// ./src/components/reports/temparature_monitoring/index.js